import { useId } from 'react'
import { ChevronDown } from 'lucide-react'

const SIZES = {
  md: { header: 'px-4 py-3 text-sm', body: 'px-4 pb-4 pt-3' },
  sm: { header: 'px-3 py-2 text-xs', body: 'px-3 pb-3 pt-2' },
}

/**
 * A collapsible card for one choice group; the header names the current pick so a closed card still says what it holds.
 * The heading holds the disclosure (`<hN><button aria-expanded aria-controls>`). `summaryTone="warning"` flags a pick
 * that blocks the operator (exposed as `data-tone` on the summary).
 */
export default function PrepackChoiceCard({
  title,
  summary,
  summaryTone = 'muted',
  open,
  onToggle,
  headingLevel = 2,
  size = 'md',
  grow = false,
  keepMounted = false,
  children,
}) {
  const id = useId()
  const Heading = headingLevel === 3 ? 'h3' : 'h2'
  const sizing = SIZES[size] || SIZES.md
  const summaryColour = summaryTone === 'warning' ? 'font-medium text-[var(--wf-color-warning)]' : 'font-normal text-[var(--prepacks-muted)]'
  return (
    <section className={`rounded-xl border border-[var(--prepacks-border)] bg-[var(--prepacks-bg)] ${grow && open ? 'flex flex-col lg:min-h-[236px] lg:flex-1 lg:overflow-hidden' : 'shrink-0'}`}>
      <Heading className="m-0">
        <button type="button" aria-expanded={open} aria-controls={`${id}-body`} onClick={onToggle} className={`flex w-full items-center gap-2 ${sizing.header} text-left font-semibold hover:bg-[var(--prepacks-raised)] ${open ? 'rounded-t-xl' : 'rounded-xl'}`}>
          <span className="shrink-0">{title}</span>
          {summary ? <span data-tone={summaryTone} className={`min-w-0 flex-1 truncate text-right text-xs ${summaryColour}`}>{summary}</span> : <span className="flex-1" />}
          <ChevronDown className={`h-4 w-4 shrink-0 text-[var(--prepacks-muted)] transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
      </Heading>
      {/* `keepMounted` keeps a folded card's body (hidden), so work running in it, like an upload, isn't dropped. */}
      {open || keepMounted ? <div id={`${id}-body`} hidden={!open} className={`border-t border-[var(--prepacks-border)] ${sizing.body} ${grow ? 'flex min-h-0 flex-col lg:flex-1' : ''}`}>{children}</div> : null}
    </section>
  )
}
